import type { DivisionId } from '@multirx/shared/types/division.js';
import {
  DEFAULT_SEASON_MEETING_COUNT,
  normalizeSeasonMeetingCount,
  type SeasonMeetingCount,
} from '@multirx/shared/types/championship.js';

const CLIENT_ID_KEY = 'multirx-client-id';
const MEETING_COUNT_KEY = 'multirx-season-meeting-count';
const LAST_DRIVER_KEY = 'multirx-last-driver-id';
const WATCHED_DIVISION_KEY = 'multirx-watched-division';

export function getClientClaimerId(): string {
  let id = localStorage.getItem(CLIENT_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(CLIENT_ID_KEY, id);
  }
  return id;
}

export function getLastChosenDriverId(): number | null {
  const raw = localStorage.getItem(LAST_DRIVER_KEY);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? null : n;
}

export function setLastChosenDriverId(driverId: number): void {
  localStorage.setItem(LAST_DRIVER_KEY, String(driverId));
}

export function getWatchedDivision(): DivisionId {
  const raw = localStorage.getItem(WATCHED_DIVISION_KEY);
  const n = parseInt(raw ?? '4', 10);
  if (n >= 1 && n <= 4) return n as DivisionId;
  return 4;
}

export function setWatchedDivision(division: DivisionId): void {
  localStorage.setItem(WATCHED_DIVISION_KEY, String(division));
}

export function getPreferredMeetingCount(): SeasonMeetingCount {
  return normalizeSeasonMeetingCount(localStorage.getItem(MEETING_COUNT_KEY));
}

export function setPreferredMeetingCount(count: SeasonMeetingCount): void {
  localStorage.setItem(MEETING_COUNT_KEY, String(count));
}

export function defaultMeetingCount(): SeasonMeetingCount {
  return DEFAULT_SEASON_MEETING_COUNT;
}

export function parseCourseHashParams(): {
  meetingSession: number | null;
  driverId: number | null;
  circuitId: number | null;
  testRace: boolean;
} {
  const raw = window.location.hash.replace(/^#/, '');
  const qs = raw.includes('?') ? raw.split('?')[1] : '';
  const params = new URLSearchParams(qs);
  const meetingSession = params.get('meetingSession');
  const driverId = params.get('driverId') ?? params.get('asDriver');
  const circuitId = params.get('circuit');
  return {
    meetingSession: meetingSession ? parseInt(meetingSession, 10) : null,
    driverId: driverId ? parseInt(driverId, 10) : null,
    circuitId: circuitId ? parseInt(circuitId, 10) : null,
    testRace: params.get('testRace') === '1',
  };
}

export function buildCourseHash(meetingSession: number, driverId: number): string {
  return `course?meetingSession=${meetingSession}&driverId=${driverId}`;
}

export function buildBoardHash(meetingSession: number, driverId: number): string {
  return `plateau?meetingSession=${meetingSession}&driverId=${driverId}`;
}

export function parseBoardHashParams(): {
  meetingSession: number | null;
  driverId: number | null;
} {
  const raw = window.location.hash.replace(/^#/, '');
  const qs = raw.includes('?') ? raw.split('?')[1] : '';
  const params = new URLSearchParams(qs);
  const meetingSession = params.get('meetingSession');
  const driverId = params.get('driverId');
  return {
    meetingSession: meetingSession ? parseInt(meetingSession, 10) : null,
    driverId: driverId ? parseInt(driverId, 10) : null,
  };
}
